import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiService } from '../../services/api'
import { ReportTemplate } from '../../components/reports/ReportTemplateManager'
import { ReportHistoryItem } from '../../components/reports/ReportHistory'
import { ScheduledReport } from '../../components/reports/ScheduledReports'
import { ReportConfig } from '../../components/reports/ReportBuilder'

interface ReportsState {
  templates: ReportTemplate[]
  history: ReportHistoryItem[]
  scheduledReports: ScheduledReport[]
  loading: {
    templates: boolean
    history: boolean
    scheduled: boolean
    generating: boolean
  }
  error: string | null
}

const initialState: ReportsState = {
  templates: [],
  history: [],
  scheduledReports: [],
  loading: {
    templates: false,
    history: false,
    scheduled: false,
    generating: false
  },
  error: null
}

// Async thunks
export const fetchReportTemplates = createAsyncThunk(
  'reports/fetchTemplates',
  async () => {
    const response = await apiService.get<ReportTemplate[]>('/reports/templates')
    return response
  }
)

export const createReportTemplate = createAsyncThunk(
  'reports/createTemplate',
  async (template: Omit<ReportTemplate, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => {
    const response = await apiService.post<ReportTemplate>('/reports/templates', template)
    return response
  }
)

export const updateReportTemplate = createAsyncThunk(
  'reports/updateTemplate',
  async ({ id, template }: { id: string; template: Partial<ReportTemplate> }) => {
    const response = await apiService.put<ReportTemplate>(`/reports/templates/${id}`, template)
    return response
  }
)

export const deleteReportTemplate = createAsyncThunk(
  'reports/deleteTemplate',
  async (templateId: string) => {
    await apiService.delete(`/reports/templates/${templateId}`)
    return templateId
  }
)

export const generateReport = createAsyncThunk(
  'reports/generate',
  async (config: ReportConfig) => {
    const response = await apiService.post<ReportHistoryItem>('/reports/generate', config)
    return response
  }
)

export const fetchReportHistory = createAsyncThunk(
  'reports/fetchHistory',
  async (params?: { limit?: number; offset?: number }) => {
    const response = await apiService.get<ReportHistoryItem[]>('/reports/history', { params })
    return response
  }
)

// Removed downloadReport thunk to avoid blob serialization issues
// Download is now handled directly in the component

export const deleteReportHistory = createAsyncThunk(
  'reports/deleteHistory',
  async (reportId: string) => {
    await apiService.delete(`/reports/history/${reportId}`)
    return reportId
  }
)

export const fetchScheduledReports = createAsyncThunk(
  'reports/fetchScheduled',
  async () => {
    const response = await apiService.get<ScheduledReport[]>('/reports/scheduled')
    return response
  }
)

export const createScheduledReport = createAsyncThunk(
  'reports/createScheduled',
  async (scheduledReport: Omit<ScheduledReport, 'id' | 'createdAt' | 'createdBy' | 'nextRun'>) => {
    const response = await apiService.post<ScheduledReport>('/reports/scheduled', scheduledReport)
    return response
  }
)

export const updateScheduledReport = createAsyncThunk(
  'reports/updateScheduled',
  async ({ id, scheduledReport }: { id: string; scheduledReport: Partial<ScheduledReport> }) => {
    const response = await apiService.put<ScheduledReport>(`/reports/scheduled/${id}`, scheduledReport)
    return response
  }
)

export const deleteScheduledReport = createAsyncThunk(
  'reports/deleteScheduled',
  async (reportId: string) => {
    await apiService.delete(`/reports/scheduled/${reportId}`)
    return reportId
  }
)

export const runScheduledReport = createAsyncThunk(
  'reports/runScheduled',
  async (reportId: string) => {
    const response = await apiService.post<any>(`/reports/scheduled/${reportId}/run`)
    return response
  }
)

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    updateReportStatus: (state, action: PayloadAction<{ reportId: string; status: string }>) => {
      const report = state.history.find(r => r.id === action.payload.reportId)
      if (report) {
        report.status = action.payload.status as any
      }
    }
  },
  extraReducers: (builder) => {
    // Report Templates
    builder
      .addCase(fetchReportTemplates.pending, (state) => {
        state.loading.templates = true
        state.error = null
      })
      .addCase(fetchReportTemplates.fulfilled, (state, action) => {
        state.loading.templates = false
        state.templates = Array.isArray(action.payload) ? action.payload : []
      })
      .addCase(fetchReportTemplates.rejected, (state, action) => {
        state.loading.templates = false
        state.templates = []
        state.error = action.error.message || 'Failed to fetch report templates'
      })

    builder
      .addCase(createReportTemplate.fulfilled, (state, action) => {
        state.templates.push(action.payload)
      })
      .addCase(updateReportTemplate.fulfilled, (state, action) => {
        const index = state.templates.findIndex(t => t.id === action.payload.id)
        if (index !== -1) {
          state.templates[index] = action.payload
        }
      })
      .addCase(deleteReportTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(t => t.id !== action.payload)
      })

    // Report Generation
    builder
      .addCase(generateReport.pending, (state) => {
        state.loading.generating = true
        state.error = null
      })
      .addCase(generateReport.fulfilled, (state, action) => {
        state.loading.generating = false
        state.history.unshift(action.payload)
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.loading.generating = false
        state.error = action.error.message || 'Failed to generate report'
      })

    // Report History
    builder
      .addCase(fetchReportHistory.pending, (state) => {
        state.loading.history = true
        state.error = null
      })
      .addCase(fetchReportHistory.fulfilled, (state, action) => {
        state.loading.history = false
        state.history = Array.isArray(action.payload) ? action.payload : []
      })
      .addCase(fetchReportHistory.rejected, (state, action) => {
        state.loading.history = false
        state.history = []
        state.error = action.error.message || 'Failed to fetch report history'
      })

    builder
      .addCase(deleteReportHistory.fulfilled, (state, action) => {
        state.history = state.history.filter(r => r.id !== action.payload)
      })

    // Scheduled Reports
    builder
      .addCase(fetchScheduledReports.pending, (state) => {
        state.loading.scheduled = true
        state.error = null
      })
      .addCase(fetchScheduledReports.fulfilled, (state, action) => {
        state.loading.scheduled = false
        state.scheduledReports = Array.isArray(action.payload) ? action.payload : []
      })
      .addCase(fetchScheduledReports.rejected, (state, action) => {
        state.loading.scheduled = false
        state.scheduledReports = []
        state.error = action.error.message || 'Failed to fetch scheduled reports'
      })

    builder
      .addCase(createScheduledReport.fulfilled, (state, action) => {
        state.scheduledReports.push(action.payload)
      })
      .addCase(updateScheduledReport.fulfilled, (state, action) => {
        const index = state.scheduledReports.findIndex(r => r.id === action.payload.id)
        if (index !== -1) {
          state.scheduledReports[index] = action.payload
        }
      })
      .addCase(deleteScheduledReport.fulfilled, (state, action) => {
        state.scheduledReports = state.scheduledReports.filter(r => r.id !== action.payload)
      })
  }
})

export const { clearError, updateReportStatus } = reportsSlice.actions
export default reportsSlice.reducer